// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// Enrollment review notes (mig 436) — TanStack Query hooks (E-02: components never call api.* directly).
// The thread is paged on the SERVER; each page is its own cache entry.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addEnrollmentReviewNote, listEnrollmentReviewNotes } from '../../api/carerxPatient'

export const reviewNoteKeys = {
  patient: (patientId: string) => ['carerx', 'enrollment-review-notes', patientId] as const,
  page: (patientId: string, page: number, pageSize: number) =>
    [...reviewNoteKeys.patient(patientId), page, pageSize] as const,
}

export function useEnrollmentReviewNotes(patientId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: reviewNoteKeys.page(patientId, page, pageSize),
    queryFn: () => listEnrollmentReviewNotes(patientId, page, pageSize),
    placeholderData: keepPreviousData,   // paging keeps the old page on screen until the next lands
  })
}

/** Adds a note. Every cached page of this patient's thread goes stale WITHOUT an immediate refetch:
 *  the new note lands on page 1, so the caller shows page 1 (`onAdded`) and only that page is fetched. */
export function useAddEnrollmentReviewNote(patientId: string, onAdded: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (note: string) => addEnrollmentReviewNote(patientId, note),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: reviewNoteKeys.patient(patientId), refetchType: 'none' })
      onAdded()
    },
  })
}
